import { useEffect, useRef, useState, useCallback } from "react";
import api, { apiError, getToken } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Loading, EmptyState } from "@/components/common/PageHeader";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle, Bell, CheckSquare, Package, TrendingDown, CheckCheck,
  BellOff, Target, Wallet, Trash2, Wifi, WifiOff,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

const KIND = {
  approval:      { border: "border-l-blue-500",    badge: "bg-blue-100 text-blue-700",       icon: CheckSquare },
  low_stock:     { border: "border-l-orange-500",  badge: "bg-orange-100 text-orange-700",   icon: Package },
  budget:        { border: "border-l-red-500",     badge: "bg-red-100 text-red-700",         icon: Target },
  cash_variance: { border: "border-l-yellow-500",  badge: "bg-yellow-100 text-yellow-700",   icon: Wallet },
  wastage:       { border: "border-l-purple-500",  badge: "bg-purple-100 text-purple-700",   icon: TrendingDown },
  success:       { border: "border-l-emerald-500", badge: "bg-emerald-100 text-emerald-700", icon: CheckCheck },
  default:       { border: "border-l-neutral-400", badge: "bg-neutral-100 text-neutral-600", icon: Bell },
};

const TABS = [
  { value: "all", label: "All" },
  { value: "unread", label: "Unread" },
  { value: "approval", label: "Approvals" },
  { value: "low_stock", label: "Stock" },
  { value: "budget", label: "Budget" },
  { value: "cash_variance", label: "Cash" },
  { value: "wastage", label: "Wastage" },
];

function bucketOf(iso) {
  if (!iso) return "Earlier";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, now)) return "Today";
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (sameDay(d, y)) return "Yesterday";
  return "Earlier";
}

export default function Notifications() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [connected, setConnected] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/notifications"); setRows(data || []); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Real-time WebSocket with auto-reconnect + polling fallback
  useEffect(() => {
    let closed = false;
    const connect = () => {
      const backend = process.env.REACT_APP_BACKEND_URL;
      if (!backend) return;
      const base = backend.replace(/^http/, "ws");
      const ws = new WebSocket(`${base}/api/notifications/ws?token=${getToken()}`);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "notification") {
            setRows((prev) => (prev.some((n) => n.id === msg.data.id) ? prev : [{ ...msg.data, _new: true }, ...prev]));
          }
        } catch (e) { console.error("Notification WS parse failed", e); }
      };
      ws.onclose = () => {
        setConnected(false);
        if (!closed) retryRef.current = setTimeout(connect, 4000);
      };
      ws.onerror = () => ws.close();
    };
    connect();
    const poll = setInterval(load, 25000); // fallback if WS blocked
    return () => { closed = true; clearTimeout(retryRef.current); clearInterval(poll); if (wsRef.current) wsRef.current.close(); };
  }, [load]);

  const readOne = async (id) => { await api.post(`/notifications/${id}/read`); setRows((s) => s.map((n) => n.id === id ? { ...n, read: true } : n)); };
  const readAll = async () => { await api.post("/notifications/read-all"); toast.success("All marked read"); setRows((s) => s.map((n) => ({ ...n, read: true }))); };
  const removeOne = async (id) => { await api.delete(`/notifications/${id}`); setRows((s) => s.filter((n) => n.id !== id)); };
  const clearRead = async () => {
    try { const { data } = await api.post("/notifications/clear-read"); toast.success(`Cleared ${data.deleted} read`); setConfirmClear(false); setRows((s) => s.filter((n) => !n.read)); }
    catch (e) { toast.error(apiError(e)); }
  };

  const unreadCount = rows.filter((n) => !n.read).length;
  const filtered = rows.filter((n) => tab === "all" ? true : tab === "unread" ? !n.read : n.kind === tab);
  const buckets = ["Today", "Yesterday", "Earlier"].map((b) => ({ b, items: filtered.filter((n) => bucketOf(n.created_at) === b) })).filter((g) => g.items.length);

  return (
    <div className="space-y-6">
      <PageHeader title="Notifications" subtitle="Real-time alerts across stock, wastage, approvals, cash and budgets.">
        <span className={cn("flex items-center gap-1.5 rounded-sm border px-2 py-1 text-xs font-medium", connected ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-neutral-200 bg-neutral-50 text-neutral-500")} data-testid="ws-status">
          {connected ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}{connected ? "Live" : "Offline"}
        </span>
      </PageHeader>

      {/* Sticky action bar */}
      <div className="sticky top-16 z-10 -mx-4 flex flex-col gap-3 border-b border-border bg-background/80 px-4 py-3 backdrop-blur-xl sm:mx-0 sm:flex-row sm:items-center sm:justify-between sm:rounded-sm sm:border sm:px-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap">
            {TABS.map((t) => <TabsTrigger key={t.value} value={t.value} data-testid={`notif-tab-${t.value}`}>{t.label}{t.value === "unread" && unreadCount > 0 ? ` (${unreadCount})` : ""}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={readAll} disabled={!unreadCount} data-testid="mark-all-read"><CheckCheck className="mr-1.5 h-4 w-4" />Mark all read</Button>
          <Button variant="outline" size="sm" onClick={() => setConfirmClear(true)} data-testid="clear-read"><Trash2 className="mr-1.5 h-4 w-4" />Clear read</Button>
        </div>
      </div>

      {loading ? <Loading /> : filtered.length === 0 ? <EmptyState icon={BellOff} title="All caught up" hint="No notifications here." /> : (
        <div className="space-y-6">
          {buckets.map((group) => (
            <div key={group.b}>
              <div className="eyebrow mb-2">{group.b}</div>
              <div className="space-y-2">
                <AnimatePresence initial={false}>
                  {group.items.map((n) => {
                    const k = KIND[n.kind] || KIND.default;
                    const Icon = k.icon;
                    return (
                      <motion.div
                        key={n.id}
                        layout
                        initial={n._new ? { opacity: 0, x: 40 } : false}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                        transition={{ duration: 0.28, ease: "easeOut" }}
                        data-testid={`notif-${n.id}`}
                        className={cn("flex items-start gap-3 rounded-sm border border-l-4 border-border bg-card p-4 transition-colors", k.border, !n.read && "bg-primary/[0.02]")}
                      >
                        <span className={cn("rounded-sm p-2", k.badge)}><Icon className="h-4 w-4" /></span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{n.title}</span>
                            {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                          </div>
                          <p className="text-sm text-muted-foreground">{n.message}</p>
                          <p className="mt-1 text-xs text-muted-foreground/70">{n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : ""}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          {!n.read && <Button variant="ghost" size="sm" className="h-8" onClick={() => readOne(n.id)} data-testid={`read-${n.id}`}>Read</Button>}
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeOne(n.id)} data-testid={`del-notif-${n.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog open={confirmClear} onOpenChange={setConfirmClear} title="Clear all read notifications?" description="Read notifications will be permanently removed. Unread ones are kept." confirmText="Clear read" destructive onConfirm={clearRead} />
    </div>
  );
}
